import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { MapPin, Phone, CheckCircle2 } from 'lucide-react';
import Seo from '../components/Seo';
import { findArea, AREAS } from '../data/areas';
import { BUSINESS, ADDRESS_LINE, SITE } from '../data/seoContent';

const CATS = [
  { to: '/hot-tubs', label: 'Hot Tubs' },
  { to: '/swim-spas', label: 'Swim Spas' },
  { to: '/saunas', label: 'Saunas' },
  { to: '/cold-plunges', label: 'Cold Plunges' },
  { to: '/spa-butler', label: 'Spa Butler Service' },
];

const areaLd = (a) => [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Hot tub, swim spa, sauna and cold plunge sales, delivery and installation in ${a.city}, FL`,
    serviceType: 'Hot tub and sauna sales, delivery, installation and service',
    provider: { '@id': `${SITE}/#business` },
    areaServed: { '@type': 'City', name: a.city, containedInPlace: { '@type': 'AdministrativeArea', name: `${a.county}, FL` } },
    url: `${SITE}/areas/${a.slug}`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: a.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

const AreaPage = () => {
  const { slug } = useParams();
  const a = findArea(slug);
  if (!a) return <Navigate to="/" replace />;
  const seo = {
    name: `${a.city}, FL`,
    parent: { name: 'Areas We Serve', path: '/areas' },
    title: `Hot Tubs, Swim Spas, Saunas & Cold Plunges in ${a.city}, FL | Leisure Wellness Naples`,
    description: `Hot tubs, swim spas, saunas and cold plunges for ${a.city}, FL — PDC Spas, ClearWater Spas, Health Mate, Finnmark and Icebound. Delivery, install & service. Showroom ${a.drive} away. Call 239-517-3135.`,
    keywords: `hot tubs ${a.city.toLowerCase()}, hot tub store ${a.city.toLowerCase()} fl, swim spas ${a.city.toLowerCase()}, saunas ${a.city.toLowerCase()}, cold plunge ${a.city.toLowerCase()}, hot tub delivery ${a.city.toLowerCase()}, hot tub service ${a.city.toLowerCase()}`,
    image: `area-${a.slug}`,
    alt: `Hot tubs and saunas in ${a.city}, FL — Leisure Wellness Naples`,
  };
  return (
    <>
      <Seo page={`/areas/${a.slug}`} seo={seo} jsonLd={areaLd(a)} />
      <div className="pt-36 pb-20 bg-gradient-to-b from-white via-[#E0FFFF] to-[#F0FFF0]" data-testid="area-page">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#F88200] mb-2">Serving {a.city} · {a.county}</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-[#0E3D95] mb-5" data-testid="area-h1">
            Hot Tubs, Swim Spas, Saunas & Cold Plunges in {a.city}, FL
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed mb-6" data-testid="area-intro">{a.intro}</p>
          <div className="flex flex-wrap gap-3 mb-10">
            <a href={`tel:${BUSINESS.tel}`} className="btn-primary inline-flex items-center gap-2 px-6 py-3" data-testid="area-call-btn"><Phone size={18} /> Call {BUSINESS.phone}</a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0E3D95] text-white font-bold hover:bg-[#0b3077] transition-colors" data-testid="area-book-btn">Book a showroom visit</Link>
          </div>

          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase text-[#0E3D95] mb-4">Why {a.city} chooses Leisure Wellness Naples</h2>
          <ul className="space-y-3 mb-10">
            {a.local.map((l) => (
              <li key={l} className="flex gap-3 text-slate-700"><CheckCircle2 className="text-[#F88200] shrink-0 mt-0.5" size={20} />{l}</li>
            ))}
          </ul>

          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase text-[#0E3D95] mb-4">Shop for {a.city}</h2>
          <div className="flex flex-wrap gap-3 mb-10">
            {CATS.map((c) => (
              <Link key={c.to} to={c.to} className="px-5 py-2.5 rounded-full bg-white border border-[#0E3D95]/20 text-[#0E3D95] font-semibold hover:bg-[#0E3D95] hover:text-white transition-colors" data-testid={`area-cat-${c.to.slice(1)}`}>
                {c.label} in {a.city}
              </Link>
            ))}
          </div>

          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase text-[#0E3D95] mb-4">{a.city} questions</h2>
          {a.faq.map((f) => (
            <div key={f.q} className="mb-5">
              <h3 className="font-semibold text-[#0E3D95]">{f.q}</h3>
              <p className="text-slate-600">{f.a}</p>
            </div>
          ))}

          <div className="mt-10 p-6 rounded-xl bg-white shadow-sm border border-[#0E3D95]/10" data-testid="area-nap">
            <p className="font-bold text-[#0E3D95] text-lg">{BUSINESS.name}</p>
            <p className="text-slate-600 flex items-center gap-2"><MapPin size={16} /> {ADDRESS_LINE} — {a.drive} from {a.city}</p>
            <p className="text-slate-600 flex items-center gap-2"><Phone size={16} /> {BUSINESS.phone}</p>
          </div>

          <p className="mt-8 text-sm text-slate-500">
            Also serving:{' '}
            {AREAS.filter((x) => x.slug !== a.slug).map((x, i, arr) => (
              <span key={x.slug}><Link to={`/areas/${x.slug}`} className="text-[#0E3D95] hover:text-[#F88200] underline">{x.city}</Link>{i < arr.length - 1 ? ' · ' : ''}</span>
            ))}
          </p>
        </div>
      </div>
    </>
  );
};

export default AreaPage;
